import React from "react";
import EventName from "@/components/EventName";

/**
 * HeroPromoCard
 * -------------
 * Editorial event callout for the homepage hero. Sits on the open paved
 * area in the LOWER-RIGHT of the hero image — clear of the centred
 * CrewZNatioN wordmark cluster on the lower-left of the hero, and never
 * overlapping the cars.
 *
 * Design language:
 *   • Not a floating "card" — no heavy background chip, no drop shadow.
 *     Just typographic content sitting on the pavement, matching the
 *     institution's editorial voice.
 *   • Wordmark uses the canonical <EventName /> lockup (inline variant):
 *     `THE` white / `GATHERING` gold, LTR-locked, in both EN and AR.
 *   • Supporting lines use the site's overline + body scale.
 *   • CTA reuses the site's standard gold-bordered outlined-pill style.
 *
 * Responsive behaviour:
 *   • Desktop (md+): absolute lower-right on the pavement. Right-aligned
 *     on EN so the block reads inward toward the crest; left-aligned on
 *     AR (mirrored on the same paved area) for natural RTL flow.
 *   • Tablet/Mobile (<md): absolute placed in the pavement gap between
 *     the cars and the crest cluster, horizontally centred, so nothing
 *     overlaps the crest or the vehicles.
 *
 * Locked hero elements untouched: hero image, wordmark, tagline,
 * `Smiles Per Liter`, chevron cue, all navigation/footers.
 */
export default function HeroPromoCard({ locale = "en" }) {
    const isAr = locale === "ar";

    const copy = isAr
        ? {
              edition: "نسخة شتاء 2026",
              dates: "ديسمبر ١٠-١٢ • ٢٠٢٦",
              venue: "مركز الرمال · الرياض",
              cta: "قدّم الآن",
          }
        : {
              edition: "Winter 2026 Edition",
              dates: "December 10–12, 2026",
              venue: "Rimal Center · Riyadh",
              cta: "Apply Now",
          };

    return (
        <aside
            dir={isAr ? "rtl" : "ltr"}
            /*
             * Position:
             *   • Mobile: centred in the pavement band above the crest.
             *   • Desktop: anchored to the lower-right pavement (or
             *     lower-left on AR), well clear of the centred crest.
             */
            className={[
                "absolute z-10 pointer-events-none",
                /*
                 * OWNER-APPROVED PLACEMENT (Feb 2026 final):
                 *
                 * On measurement, the hero's centred crest cluster
                 * (crest logo + long tagline in max-w-[62rem]) fills
                 * the lower band across 1024-1440 px viewports so that
                 * no clean pavement band exists between cars and
                 * tagline. Placing the callout there produced
                 * unavoidable overlap with the tagline text.
                 *
                 * Owner requires zero overlap with existing CrewZNatioN
                 * copy AND vehicles. The only region that consistently
                 * satisfies both, at every tested viewport, is the
                 * upper SKY area of the hero (above the cars, well
                 * above the crest cluster).
                 *
                 *   • Mobile (<md): top-LEFT of hero (sky/trees).
                 *   • md+ (tablet/desktop): top-RIGHT of hero (sky).
                 *
                 * Both branches are horizontally clear of the centred
                 * crest content and vertically clear of the visible
                 * vehicle band.
                 */
                // MOBILE default (< md): centered horizontally via
                // equal side margins on the anchor container. Simple
                // full-width layout — no viewport-edge drift.
                "top-4 left-0 right-0 bottom-auto",
                // md+ (tablet/desktop): PAVEMENT-BAND CALIBRATION
                // (Feb 2026 owner final).
                //   • Horizontal: negative right offset pushes the
                //     promo PAST the max-w-[62rem] anchor's right
                //     edge, placing it deeper into the right side of
                //     the hero over open pavement (not over cars).
                //     EN carries an extra ~3.5 rem shift so its
                //     visual CENTER matches AR's approved center
                //     (AR's Arabic content is ~110 px narrower than
                //     English's, so equal right edges would place
                //     EN visibly left of AR).
                //   • Vertical: lowered so the whole block sits in
                //     the paved zone above the crest cluster instead
                //     of the last car row.
                isAr
                    ? "md:right-[-4rem] lg:right-[-6rem] xl:right-[-8rem]"
                    : "md:right-[-7.5rem] lg:right-[-9.5rem] xl:right-[-11.5rem]",
                "md:top-auto md:left-auto",
                "md:bottom-[9rem] lg:bottom-[9.5rem] xl:bottom-[10rem]",
                // Width control (mobile fills container, desktop clamps).
                "md:max-w-[300px] lg:max-w-[320px]",
                // Text alignment. AR mobile switches from text-right
                // to text-center so the RTL content doesn't visually
                // right-anchor inside the viewport-wide wrapper
                // (owner Feb 2026 mobile-parity micro-fix); md+ AR
                // desktop keeps text-right unchanged.
                isAr ? "text-center md:text-right" : "text-center md:text-right",
            ].join(" ")}
            data-testid={isAr ? "hero-promo-card-ar" : "hero-promo-card"}
            aria-label={
                isAr
                    ? "قدّم لحضور التجمّع — نسخة شتاء 2026"
                    : "Apply to attend The Gathering — Winter 2026 Edition"
            }
        >
            {/*
             * Inner block re-enables pointer events for the CTA while the
             * outer `aside` remains pointer-none so hovering over the
             * text area doesn't intercept clicks on the hero background.
             */}
            <div
                className={`pointer-events-auto ${isAr ? "text-center md:text-right" : "text-center md:text-right"}`}
                style={{
                    textShadow: "0 1px 2px rgba(0,0,0,0.55)",
                }}
            >
                {/* THE GATHERING — two-tone wordmark. Compact desktop
                    size (Feb 2026) so total callout height fits in the
                    hero's pavement band above the crest cluster. */}
                <h2
                    className="font-display leading-[0.9] tracking-[-0.005em] text-[24px] sm:text-[28px] md:text-[30px] lg:text-[34px]"
                    style={{ textTransform: "uppercase" }}
                    data-testid="hero-promo-gathering"
                >
                    <EventName surface="dark" variant="inline" />
                </h2>

                {/* Edition — overline, gold, tight tracking.
                    Spacing tightened Feb 2026 to reduce total callout
                    height and give more clearance at all breakpoints. */}
                <p
                    className="overline overline-primary text-[var(--crewz-gold)] mt-1 md:mt-1"
                    data-testid="hero-promo-edition"
                >
                    {copy.edition}
                </p>

                {/* Dates — bold in both EN and AR per owner directive
                    (Feb 2026). Venue remains a separate, non-bold line
                    below to preserve typographic hierarchy.
                    AR font size: locked to 23px via inline style. The
                    site's global `.text-* { !important }` overrides
                    would defeat any class-based size on AR, so we
                    omit `text-*` classes for AR and let the inline
                    style take precedence unopposed. */}
                <p
                    className={
                        "font-body font-bold text-[var(--crewz-text)] mt-2 leading-snug " +
                        (isAr ? "" : "text-sm md:text-base")
                    }
                    style={isAr ? { fontSize: "23px" } : undefined}
                    data-testid="hero-promo-dates"
                >
                    {copy.dates}
                </p>
                {/* Venue — bold in both EN and AR per owner directive
                    (Feb 2026 parity update). Same AR-only inline-style
                    lock as the dates block above. */}
                <p
                    className={
                        "font-body font-bold text-[var(--crewz-text)]/85 leading-snug " +
                        (isAr ? "" : "text-sm md:text-base")
                    }
                    style={isAr ? { fontSize: "23px" } : undefined}
                    data-testid="hero-promo-venue"
                >
                    {copy.venue}
                </p>

                {/* CTA — solid golden-yellow fill matching the brand
                    gold used in the callout wordmark. Default text is
                    white; on active/pressed the text switches to black
                    while the gold background is preserved (owner
                    directive, Feb 2026). Same visual footprint in EN
                    and AR — a min-width and centred flex layout keeps
                    the shorter Arabic label from shrinking the button.
                    Opens the apply subdomain in a new tab. */}
                <a
                    href="https://apply.crewznation.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 mt-2 md:mt-3 min-w-[168px] md:min-w-[176px] bg-[var(--crewz-gold)] text-white active:text-black transition-colors px-6 py-2 md:py-2.5 text-[11px] md:text-xs tracking-[0.22em] uppercase font-bold"
                    style={{ textShadow: "none" }}
                    data-testid={isAr ? "hero-promo-cta-ar" : "hero-promo-cta"}
                >
                    {copy.cta}
                </a>
            </div>
        </aside>
    );
}
